/**
 * The books a child is reading, and how they get there.
 *
 * Sam's correction to the first version: the point is not to photograph a
 * cover and have a model guess. It is to read the thirteen digits under the
 * barcode and get the **exact** edition — this printing, this page count,
 * this cover. "The Hobbit" matches forty printings; 9780547928227 matches
 * one.
 *
 * **Three ways in, and the plain one is first.**
 *
 *   1. **Type the number.** Works on every device, and the digits are
 *      printed in figures under every barcode, which is why they are printed
 *      that way. A USB barcode scanner also works here — it pretends to be a
 *      keyboard and types the digits for you.
 *   2. **The camera.** Works on any device with one. The browser's own
 *      `BarcodeDetector` where it exists — Chrome and Edge on Android,
 *      macOS and ChromeOS — and ZXing everywhere else, which is what makes
 *      this work on Windows and on an iPad. ZXing is downloaded only when
 *      somebody actually scans.
 *   3. **Type the name**, for a library copy with a sticker over the ISBN. A
 *      book a child loves is not less real for being hard to scan.
 *
 * **Looking up adds nothing.** The cover comes back first and the book goes
 * on the shelf only when somebody says so — a scanner firing on every frame
 * would otherwise add four copies of the same book.
 *
 * **A finished book stays on the shelf.** A list that empties as you finish
 * things is a to-do list. A list that fills up is a record of what you have
 * read, and that is the one worth looking at. Same rule as the garden
 * (D-048), applied to books.
 *
 * **A counter and a gate** (Sam, 11 Oct 2026). Each book shows how many
 * times words were picked from it; every approved book is used once before
 * any is used twice, and the next one is marked. Books that came from the
 * library without anyone choosing them wait at the top for a grown-up's yes.
 * A film or a show can sit on the same shelf, typed in by name.
 */
import { useEffect, useRef, useState } from 'react'
import { api, type Book, type BookLookup, type Child, type Shelf } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, inputStyle } from './ui'
import { Trace, TraceRegion } from '../lib/trace'
import {
  canScanBarcodes, closeCamera, GUIDE, makeReader, openCamera,
  type Reader,
} from '../lib/barcode'
import { PageWords } from './PageWords'
import { dateLocale, fromServer, t } from '../lib/i18n'

type Sort = 'newest' | 'oldest' | 'title' | 'author' | 'used'
type Owned = Book & { owner: string }
type Kind = NonNullable<Book['kind']>
type Round = Omit<Shelf, 'books'>

// Functions, not constants: t() reads the language as the screen draws.
const sorts = (): [Sort, string][] => [
  ['newest', t('Newest added')], ['oldest', t('Oldest added')], ['title', t('Title A–Z')],
  ['author', t('Author A–Z')], ['used', t('Least used')],
]

const KIND_ICON: Record<Kind, string> = { book: '📕', film: '🎬', show: '📺' }
const kindName = (k: Kind): string => ({ book: t('Book'), film: t('Film'), show: t('TV show') })[k]
const namePlaceholder = (k: Kind): string =>
  ({ book: t("The book's name"), film: t("The film's name"), show: t("The TV show's name") })[k]

/** "11 Oct 2025", from a date the server sends as 2025-10-11. */
function day(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`)
  return d.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
}

/** The surname-ish part an author list sorts by: the last word of the first name. */
function authorKey(authors: string | null): string {
  const first = (authors ?? '').split(/[,;&]| and /)[0]?.trim() ?? ''
  return (first.split(/\s+/).pop() ?? '').toLowerCase() || '~'
}

function sortBooks(books: Owned[], sort: Sort): Owned[] {
  const by = [...books]
  if (sort === 'newest') by.sort((a, b) => (b.added_at || '').localeCompare(a.added_at || ''))
  if (sort === 'oldest') by.sort((a, b) => (a.added_at || '').localeCompare(b.added_at || ''))
  if (sort === 'title') by.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }))
  if (sort === 'author') {
    by.sort((a, b) => authorKey(a.authors).localeCompare(authorKey(b.authors))
      || a.title.localeCompare(b.title))
  }
  if (sort === 'used') {
    by.sort((a, b) => (a.times_used ?? 0) - (b.times_used ?? 0)
      || String(a.last_used_on ?? '').localeCompare(String(b.last_used_on ?? ''))
      || a.title.localeCompare(b.title))
  }
  return by
}

/**
 * Every book, for one child or for everyone, in the order the grown-up
 * chooses (Sam, 8 Oct 2026: "list all entered books, sortable by date
 * entered, and book or author name").
 */
export function ReadingList({ children }: { children: Child[] }) {
  const [who, setWho] = useState<string>(children[0]?.learner_id ?? '')
  const [all, setAll] = useState<Owned[] | null>(null)
  const [rounds, setRounds] = useState<Record<string, Round>>({})
  const [sort, setSort] = useState<Sort>('newest')

  const load = () => {
    if (!who) return
    const ids = who === 'all' ? children.map(c => c.learner_id) : [who]
    Promise.all(ids.map(id => api.readingList(id)
      .then(r => ({ id, r }))
      .catch(() => ({ id, r: null as Shelf | null }))))
      .then(lists => {
        setAll(lists.flatMap(({ id, r }) => (r?.books ?? []).map(b => ({ ...b, owner: id }))))
        const next: Record<string, Round> = {}
        for (const { id, r } of lists) {
          if (r) next[id] = { round: r.round, used_this_round: r.used_this_round, approved: r.approved, next_book_id: r.next_book_id }
        }
        setRounds(next)
      })
  }
  useEffect(load, [who])

  if (!children.length) return null
  const nameOf = (id: string) => children.find(c => c.learner_id === id)?.display_name ?? ''
  // Waiting ones sit in their own box at the top, not in the list.
  const waiting = all?.filter(b => b.approval === 'waiting') ?? []
  const shelf = all?.filter(b => b.approval !== 'waiting') ?? null
  const nextIds = new Set(Object.values(rounds).map(r => r.next_book_id).filter(Boolean))

  return (
    <TraceRegion id="T23" note="looked up by ISBN, free, no key">
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {children.length > 1 ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[...children.map(c => ({ learner_id: c.learner_id, display_name: c.display_name })),
            { learner_id: 'all', display_name: t('Everyone') }].map(c => {
            const on = c.learner_id === who
            return (
              <button
                key={c.learner_id}
                type="button"
                onClick={() => { setWho(c.learner_id); setAll(null) }}
                style={{
                  fontFamily: 'inherit', fontSize: 15, fontWeight: on ? 800 : 600,
                  color: on ? theme.orangeDark : theme.muted,
                  background: on ? theme.orangePale : theme.card,
                  border: `2px solid ${on ? theme.orange : theme.border}`,
                  borderRadius: radius.pill, padding: '8px 14px',
                  minHeight: TAP_MIN, cursor: 'pointer',
                }}
              >
                {c.display_name}
              </button>
            )
          })}
        </div>
      ) : null}

      {who !== 'all' ? <AddABook learnerId={who} onAdded={load} empty={shelf !== null && shelf.length === 0 && !waiting.length} /> : null}

      {waiting.length ? <Waiting books={waiting} nameOf={who === 'all' ? nameOf : undefined} onChanged={load} /> : null}

      {Object.entries(rounds).map(([id, r]) => r.round ? (
        <RoundLine key={id} round={r} name={who === 'all' ? nameOf(id) : ''}
          next={all?.find(b => b.book_id === r.next_book_id)?.title ?? ''} />
      ) : null)}

      {shelf && shelf.length ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: theme.text, marginRight: 4 }}>
            {shelf.length === 1 ? t('1 book') : t('{n} books', { n: shelf.length })}
          </span>
          <span style={{ fontSize: 14, color: theme.muted }}>{t('Sort:')}</span>
          {sorts().map(([key, label]) => (
            <button key={key} type="button" onClick={() => setSort(key)} aria-pressed={sort === key}
              style={{
                fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                padding: '6px 12px', borderRadius: radius.pill, minHeight: 36,
                border: `1.5px solid ${sort === key ? theme.blue : theme.border}`,
                background: sort === key ? theme.blue : theme.card,
                color: sort === key ? '#fff' : theme.text2,
              }}>{label}</button>
          ))}
        </div>
      ) : null}

      {shelf === null ? (
        <Card><span style={{ color: theme.muted }}>{t('Looking…')}</span></Card>
      ) : shelf.length === 0 ? (
        who === 'all' ? (
          <p style={{ margin: 0, color: theme.muted, fontSize: 15 }}>
            {t('No books yet. Pick a child above to add one.')}
          </p>
        ) : null
      ) : (
        sortBooks(shelf, sort).map(book => (
          <div key={book.book_id}>
            {who === 'all' ? (
              <div style={{ fontSize: 12, fontWeight: 800, color: theme.faint, margin: '0 0 4px 4px',
                            textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {nameOf(book.owner)}
              </div>
            ) : null}
            <BookRow book={book} learnerId={book.owner} onChanged={load} next={nextIds.has(book.book_id)} />
          </div>
        ))
      )}
    </div>
    </TraceRegion>
  )
}


// ------------------------------------------------------------- adding one

function AddABook({
  learnerId, onAdded, empty = false,
}: { learnerId: string; onAdded: () => void; empty?: boolean }) {
  const [typed, setTyped] = useState('')
  const [found, setFound] = useState<BookLookup | null>(null)
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)

  const digits = typed.replace(/\D/g, '')

  const look = async (isbn: string) => {
    if (!isbn || busy) return
    setBusy(true)
    setSaid(null)
    setFound(null)
    try {
      const book = await api.lookUpIsbn(isbn)
      setFound(book)
      setTitle(book.title)
    } catch (err) {
      setSaid(fromServer(err, 'That did not work.'))
    } finally {
      setBusy(false)
    }
  }

  const put = async (body: { isbn?: string; title?: string; kind?: string }) => {
    setBusy(true)
    try {
      const book = await api.addBook(learnerId, body)
      setSaid(t('“{title}” is on the shelf.', { title: book.title }))
      setFound(null)
      setTyped('')
      onAdded()
    } catch (err) {
      setSaid(fromServer(err, 'That did not work.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <div style={{ fontSize: 17, fontWeight: 800, color: theme.text,
                    marginBottom: 4 }}>
        {empty ? t('Add the first book') : t('Add a book')}
      </div>
      {/* "No books yet" lives here now, in the add box, rather than as a
          card of its own under it (Sam, 8 Oct 2026). */}
      <p style={{ margin: '0 0 14px', fontSize: 14, color: theme.faint,
                  lineHeight: 1.6 }}>
        {empty
          ? t('No books yet. Type the 13 digits under the barcode on the back, or scan it. We look it up in two free book catalogues (Open Library and Google Books); nothing is guessed.')
          : t('Type the 13 digits under the barcode on the back, or scan it. We look it up in two free book catalogues (Open Library and Google Books); nothing is guessed.')}
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                    alignItems: 'center' }}>
        <input
          style={{ ...inputStyle, flex: '1 1 200px', width: 'auto',
                   fontSize: 20, letterSpacing: 1 }}
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value)
            setSaid(null)
            // A scanner types thirteen digits and stops. Firing at thirteen
            // means the parent never has to press anything.
            const clean = e.target.value.replace(/\D/g, '')
            if (clean.length === 13) void look(clean)
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') void look(digits) }}
          placeholder="978…"
          inputMode="numeric"
          autoComplete="off"
          maxLength={20}
        />
        <Button tone="quiet" disabled={!digits || busy}
                onClick={() => void look(digits)}>
          {busy ? t('Looking…') : t('Look it up')}
        </Button>
        {canScanBarcodes() ? (
          <Button tone="ghost" onClick={() => setScanning(true)} disabled={busy}>
            📷 {t('Scan it')}
          </Button>
        ) : null}
      </div>

      {!canScanBarcodes() ? (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: theme.fainter,
                    lineHeight: 1.5 }}>
          {t('There is no camera on this device. The number under the barcode works everywhere.')}
        </p>
      ) : null}

      {scanning ? (
        <Scanner
          onFound={(isbn) => { setScanning(false); setTyped(isbn); void look(isbn) }}
          onClose={() => setScanning(false)}
        />
      ) : null}

      {said ? (
        <p style={{ margin: '12px 0 0', fontSize: 15, color: theme.text2,
                    lineHeight: 1.5 }}>
          {said}
        </p>
      ) : null}

      {/* What came back, before anything is added. */}
      {found ? (
        <div style={{ display: 'flex', gap: 14, marginTop: 16,
                      alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Cover url={found.cover_url} />
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <input
              style={{ ...inputStyle, fontSize: 17, fontWeight: 700 }}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
            />
            <div style={{ fontSize: 15, color: theme.text2, marginTop: 6 }}>
              <Trace id="T23.authors">{found.authors || t('author unknown')}</Trace>
            </div>
            <div style={{ fontSize: 13, color: theme.faint, marginTop: 4 }}>
              {[found.published, found.page_count ? t('{n} pages', { n: found.page_count }) : null,
                found.source]
                .filter(Boolean).join(' · ')}
            </div>
            {found.description ? (
              <p style={{ margin: '8px 0 0', fontSize: 14, color: theme.muted,
                          lineHeight: 1.5 }}>
                {found.description}
              </p>
            ) : null}
            <p style={{ margin: '8px 0 0', fontSize: 13, color: theme.fainter }}>
              {t('The name is editable — catalogue entries are written by volunteers and are sometimes in the wrong case.')}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 12,
                          flexWrap: 'wrap' }}>
              <Button tone="child" disabled={busy}
                      onClick={() => void put({ isbn: found.isbn, title })}>
                {t('Add it')}
              </Button>
              <Button tone="ghost" onClick={() => setFound(null)}>
                {t('Not that one')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <ByName onAdd={(name, kind) => void put({ title: name, kind })} busy={busy} />
    </Card>
  )
}


/** The way in for a library copy with a sticker over the ISBN, and for a
 *  film or a show, which have no ISBN at all. */
function ByName({
  onAdd, busy,
}: { onAdd: (title: string, kind: Kind) => void; busy: boolean }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<Kind>('book')

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
          color: theme.muted, background: 'transparent', border: 'none',
          padding: '12px 0 0', cursor: 'pointer', textAlign: 'left',
        }}
      >
        {t('No barcode, or a film or show? Type its name →')}
      </button>
    )
  }
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
      <div role="radiogroup" aria-label={t('What is it?')} style={{ display: 'flex', gap: 6 }}>
        {(['book', 'film', 'show'] as const).map(k => (
          <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)}
            style={{
              fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              padding: '6px 12px', borderRadius: radius.pill, minHeight: 36,
              border: `1.5px solid ${kind === k ? theme.blue : theme.border}`,
              background: kind === k ? theme.blue : theme.card,
              color: kind === k ? '#fff' : theme.text2,
            }}>
            {KIND_ICON[k]} {kindName(k)}
          </button>
        ))}
      </div>
      <input
        style={{ ...inputStyle, flex: '1 1 200px', width: 'auto' }}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) { onAdd(name.trim(), kind); setName('') }
        }}
        placeholder={namePlaceholder(kind)}
        maxLength={200}
        autoFocus
      />
      <Button tone="quiet" disabled={!name.trim() || busy}
              onClick={() => { onAdd(name.trim(), kind); setName('') }}>
        {t('Add')}
      </Button>
    </div>
  )
}


// --------------------------------------------------------------- the camera

function Scanner({
  onFound, onClose,
}: { onFound: (isbn: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [engine, setEngine] = useState<Reader['engine'] | null>(null)

  useEffect(() => {
    let stream: MediaStream | null = null
    let stopped = false
    let timer = 0

    const run = async () => {
      let reader: Reader
      try {
        reader = await makeReader()
      } catch {
        setProblem(t('The barcode reader could not be loaded. Type the number instead — it is printed under the barcode.'))
        return
      }
      if (stopped) return
      setEngine(reader.engine)

      try {
        stream = await openCamera()
      } catch {
        setProblem(t('The camera could not be opened. It may be in use, or permission was refused.'))
        return
      }
      if (stopped) { closeCamera(stream); return }
      if (video.current) {
        video.current.srcObject = stream
        await video.current.play().catch(() => undefined)
      }

      // Six or seven looks a second.
      //
      // Measured rather than guessed: ZXing decodes a 1075x230 crop in about
      // 1ms, even blurred, tilted 11 degrees, unevenly lit and full of
      // noise. The copy out of the video is the expensive part, and the
      // whole thing lands well under 10ms — so the gap is what is left of
      // 150ms rather than a fixed wait, and a slow device slows down instead
      // of falling behind.
      const tick = async () => {
        if (stopped || !video.current) return
        const began = performance.now()
        const isbn = await reader.read(video.current)
        if (stopped) return
        if (isbn) { onFound(isbn); return }
        const spent = performance.now() - began
        timer = window.setTimeout(tick, Math.max(30, 150 - spent))
      }
      void tick()
    }
    void run()

    return () => {
      stopped = true
      window.clearTimeout(timer)
      closeCamera(stream)
    }
  }, [onFound])

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        position: 'relative', borderRadius: radius.md, overflow: 'hidden',
        background: theme.deep,
      }}>
        <video
          ref={video}
          muted
          playsInline
          style={{ width: '100%', display: 'block', maxHeight: 320,
                   objectFit: 'cover' }}
        />
        {/* Something to line the barcode up with. Without it people hold the
            book too far away and nothing ever decodes.

            The numbers come from GUIDE, which is also the crop the decoder
            reads. Two copies of "the middle strip" would drift apart, and
            the drift would show up as a scanner that works only when the
            barcode is somewhere other than where the box says. */}
        <div style={{
          position: 'absolute',
          left: `${GUIDE.left * 100}%`,
          right: `${(1 - GUIDE.right) * 100}%`,
          top: `${GUIDE.top * 100}%`,
          bottom: `${(1 - GUIDE.bottom) * 100}%`,
          border: `3px solid ${theme.orange}`, borderRadius: radius.sm,
          pointerEvents: 'none',
        }} />
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 14, color: theme.muted }}>
        {problem
         || (engine ? t('Hold the barcode inside the box.') : t('Starting the camera…'))}
      </p>
      <div style={{ marginTop: 10 }}>
        <Button tone="ghost" onClick={onClose}>{t('Stop the camera')}</Button>
      </div>
    </div>
  )
}


// ----------------------------------------------------------- one book, shown

function BookRow({
  book, learnerId, onChanged, next = false,
}: { book: Book; learnerId: string; onChanged: () => void; next?: boolean }) {
  const [busy, setBusy] = useState(false)
  const [takingWords, setTakingWords] = useState(false)
  const finished = book.status === 'finished'
  const declined = book.approval === 'declined'
  const notBook = !!book.kind && book.kind !== 'book'
  const used = book.times_used ?? 0

  const set = async (status: string) => {
    setBusy(true)
    try { await api.setBookStatus(book.book_id, status); onChanged() }
    finally { setBusy(false) }
  }

  return (
    <Card style={declined ? { background: theme.bg, opacity: 0.7 } : finished ? { background: theme.bg } : undefined}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start',
                    flexWrap: 'wrap' }}>
        <Cover url={book.cover_url} kind={book.kind} />
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: theme.text,
                        lineHeight: 1.3 }}>
            <Trace id="T23.title">{book.title}</Trace>
            {finished ? <span style={{ marginLeft: 8 }}>✅</span> : null}
            {next ? <Tag tone="green">{t('Next to use')}</Tag> : null}
            {declined ? <Tag tone="grey">{t('Not for us')}</Tag> : null}
          </div>
          {book.authors ? (
            <div style={{ fontSize: 15, color: theme.text2, marginTop: 3 }}>
              <Trace id="T23.authors">{book.authors}</Trace>
            </div>
          ) : null}
          <div style={{ fontSize: 13, color: theme.faint, marginTop: 4 }}>
            {[
              notBook ? kindName(book.kind!) : null,
              book.page_count ? t('{n} pages', { n: book.page_count }) : null,
              book.isbn ? `ISBN ${book.isbn}` : null,
              book.added_via === 'booklist' ? t('from the old app’s book list')
                : book.added_via === 'library' ? t('from the library')
                : !book.isbn && !notBook ? t('typed in') : null,
              book.words_from_it
                ? book.words_from_it === 1 ? t('1 word from it') : t('{n} words from it', { n: book.words_from_it })
                : null,
            ].filter(Boolean).join(' · ')}
          </div>
          <div style={{ fontSize: 14, color: used ? theme.text2 : theme.faint, marginTop: 4, fontWeight: 600 }}>
            <Trace id="T29.used_on">
              {used
                ? used === 1
                  ? t('Used once to pick words · last {date}', { date: day(book.last_used_on) })
                  : t('Used {n} times to pick words · last {date}', { n: used, date: day(book.last_used_on) })
                : t('Not used to pick words yet')}
            </Trace>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12,
                        flexWrap: 'wrap' }}>
            <Button tone="quiet" disabled={busy}
                    onClick={() => setTakingWords(v => !v)}>
              {takingWords ? t('Close') : `📷 ${t('Words from a page')}`}
            </Button>
            <Button tone="ghost" disabled={busy}
                    onClick={() => void set(finished ? 'reading' : 'finished')}>
              {finished ? t('Reading it again') : t('Finished it')}
            </Button>
            <Button tone="ghost" disabled={busy}
                    onClick={async () => {
                      setBusy(true)
                      try {
                        await api.setBookApproval(book.book_id, declined ? 'approved' : 'declined')
                        onChanged()
                      } finally { setBusy(false) }
                    }}>
              {declined ? t('Use it after all') : t('Not for us')}
            </Button>
            <Button tone="ghost" disabled={busy}
                    onClick={async () => {
                      setBusy(true)
                      try { await api.removeBook(book.book_id); onChanged() }
                      finally { setBusy(false) }
                    }}>
              {t('Take it off')}
            </Button>
          </div>
        </div>
      </div>

      {takingWords ? (
        <PageWords learnerId={learnerId} book={book}
                   onAdded={() => { setTakingWords(false); onChanged() }} />
      ) : null}
    </Card>
  )
}


/** The jacket, or a placeholder. A missing cover 404s rather than showing
 *  a blank rectangle, which is what `onError` is for. */
function Cover({ url, kind = 'book', small = false }: {
  url: string | null | undefined; kind?: Kind; small?: boolean
}) {
  const [broken, setBroken] = useState(false)
  if (!url || broken) {
    return (
      <div style={{
        flex: '0 0 auto', width: small ? 40 : 76, height: small ? 58 : 112,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: small ? 18 : 28, background: theme.bg,
        border: `1px solid ${theme.border}`, borderRadius: radius.sm,
      }}>
        {KIND_ICON[kind]}
      </div>
    )
  }
  return (
    <img
      src={url}
      alt=""
      onError={() => setBroken(true)}
      style={{
        flex: '0 0 auto', width: small ? 40 : 76, height: 'auto', maxHeight: small ? 64 : 130,
        borderRadius: radius.sm, border: `1px solid ${theme.border}`,
        display: 'block',
      }}
    />
  )
}


/** A small coloured word after a title. */
function Tag({ tone, children }: { tone: 'green' | 'grey'; children: string }) {
  return (
    <span style={{
      marginLeft: 8, fontSize: 12, fontWeight: 800, verticalAlign: 'middle',
      padding: '2px 8px', borderRadius: radius.pill,
      background: tone === 'green' ? theme.greenPale : theme.bg,
      color: tone === 'green' ? theme.greenDarker : theme.muted,
      border: `1px solid ${tone === 'green' ? theme.green : theme.border}`,
    }}>{children}</span>
  )
}


/** "Round 1: 27 of 100 books used. Next: The Haunted Hotel." */
function RoundLine({ round, name, next }: { round: Round; name: string; next: string }) {
  return (
    <Card>
      <Trace id="calc.round: one more than the fewest times any approved book was used">
        <div style={{ fontSize: 16, fontWeight: 800, color: theme.text }}>
          {name ? `${name}: ` : ''}
          {t('Round {round}: {used} of {approved} books used',
             { round: round.round ?? 0, used: round.used_this_round, approved: round.approved })}
        </div>
      </Trace>
      <div style={{ fontSize: 14, color: theme.muted, marginTop: 4, lineHeight: 1.5 }}>
        {t('Every book is used once to pick words before any is used twice.')}
        {next ? <> {t('Next:')} <b style={{ color: theme.text2 }}>{next}</b>.</> : null}
      </div>
    </Card>
  )
}


/**
 * What came in without anyone choosing it, mostly from the library. Nothing
 * is picked from these until a grown-up says yes (Sam, 11 Oct 2026). Kept
 * compact: forty library books as full cards would bury the shelf.
 */
function Waiting({
  books, nameOf, onChanged,
}: { books: Owned[]; nameOf?: (id: string) => string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const decide = async (ids: string[], approval: 'approved' | 'declined') => {
    setBusy(true)
    setProblem(null)
    try {
      for (const id of ids) await api.setBookApproval(id, approval)
    } catch (err) {
      setProblem(fromServer(err, 'That did not save.'))
    } finally {
      setBusy(false)
      onChanged()
    }
  }

  return (
    <Card style={{ border: `2px solid ${theme.orange}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: theme.text, flex: '1 1 auto' }}>
          {t('Waiting for a grown-up ({n})', { n: books.length })}
        </div>
        <Button tone="quiet" disabled={busy} onClick={() => void decide(books.map(b => b.book_id), 'approved')}>
          {t('Yes to all')}
        </Button>
      </div>
      <p style={{ margin: '4px 0 12px', fontSize: 14, color: theme.faint, lineHeight: 1.5 }}>
        {t('These came from the library. Words are only picked from books you say yes to.')}
      </p>
      {problem ? <p style={{ color: theme.red, fontSize: 14, margin: '0 0 10px' }}>{problem}</p> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {books.map(b => (
          <div key={b.book_id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Cover url={b.cover_url} kind={b.kind} small />
            <div style={{ flex: '1 1 180px', minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>
                <Trace id="T23.approval">{b.title}</Trace>
              </div>
              <div style={{ fontSize: 13, color: theme.faint }}>
                {[nameOf ? nameOf(b.owner) : null, b.authors, b.added_at ? t('borrowed {date}', { date: day(b.added_at) }) : null]

                  .filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button tone="child" disabled={busy} onClick={() => void decide([b.book_id], 'approved')}>
                {t('Yes, use it')}
              </Button>
              <Button tone="ghost" disabled={busy} onClick={() => void decide([b.book_id], 'declined')}>
                {t('Not for us')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}
